import type { OfferPageSnapshotContribution } from './offerPageSnapshotTypes'
import { toOfferPageSnapshotId } from './offerPageSnapshotText'

// Reviews are unbounded in the UI (the drawer accumulates every loaded page via
// infinite scroll, so the underlying review list can grow into the hundreds
// within one chat session) but the canonical snapshot must stay bounded — this
// caps each contribution to the first N reviews in whatever order the caller
// passes in, matching the existing "safe missing, never invented" convention
// used throughout this builder rather than trying to publish everything ever
// scrolled past.
export const OFFER_PAGE_REVIEWS_CONTRIBUTION_LIMIT = 10

export interface OfferPageReviewSummary {
  score: number
  reviewsTotal: number
}

function reviewDisplayText(review: App.Review): string {
  const rating = Number.isFinite(review.rating) ? `${review.rating}/10` : undefined
  const heading = [review.userName, rating].filter(Boolean).join(' · ')
  const body = review.review?.trim()
  return [heading, body].filter(Boolean).join('\n')
}

/**
 * Shared by UserReviewSection's inline contribution and ReviewsPanel's drawer
 * contribution so both use the identical atomId scheme for the same review —
 * required for buildOfferPageSnapshot's atomId dedup to actually collapse a
 * review visible in both into one atom instead of shipping it twice.
 *
 * `summary`, when provided, emits the one manifest-visible rating-summary atom
 * that owns the `reviews:root` target (auto-synthesised from its `targetId`
 * by `addMissingAtomTargets`). Only the inline contribution should pass this —
 * it's always mounted whenever the drawer can be open, so the drawer's review
 * atoms can safely reference `parentTargetId: 'reviews:root'` without needing
 * to build that target themselves.
 */
export function buildOfferPageReviewsContribution(
  key: string,
  reviews: Array<App.Review>,
  summary?: OfferPageReviewSummary,
): OfferPageSnapshotContribution {
  const reviewAtoms = reviews
    .slice(0, OFFER_PAGE_REVIEWS_CONTRIBUTION_LIMIT)
    .flatMap((review, index) => {
      const id = toOfferPageSnapshotId(review.id)
      if (!id) {
        return []
      }

      // "review:" (singular) is not a registered semantic id prefix on the
      // backend -- only "reviews:" (plural, matching reviews:root) is, per
      // svc-trip's assistantSurfaceProtocolSchemas.ts allowlist. A mismatch
      // here fails ensure validation for every review atom.
      const targetId = `reviews:${id}`
      return [{
        atomId: targetId,
        kind: 'review',
        label: 'Review',
        displayText: reviewDisplayText(review),
        sourcePath: `offer.reviews.${id}`,
        dataClassification: 'public_surface' as const,
        visibility: 'visible' as const,
        modelUse: 'read_tool_only' as const,
        targetId,
        parentTargetId: 'reviews:root',
        rankHint: index,
      }]
    })

  const summaryAtoms = summary ? [{
    atomId: 'reviews:root:summary',
    kind: 'review_summary',
    label: 'Reviews',
    displayText: `${summary.score}/10 average from ${summary.reviewsTotal} ${summary.reviewsTotal === 1 ? 'review' : 'reviews'}`,
    sourcePath: 'offer.reviews.summary',
    dataClassification: 'public_surface' as const,
    visibility: 'visible' as const,
    modelUse: 'manifest' as const,
    targetId: 'reviews:root',
    rankHint: 0,
  }] : []

  return {
    key,
    sourcePath: `offer.reviews.${key}`,
    recentCommittedState: [],
    atoms: [...summaryAtoms, ...reviewAtoms],
  }
}
